import React from "react";
import axios from "axios";
import {
  useNavigate,
  useSearchParams,
  type NavigateFunction,
} from "react-router-dom";
import {
  clearOauthAttemptFlag,
  clearWecomOAuthFail,
  clearWecomOauthReturnPath,
  setWecomOAuthFail,
  takeWecomOauthReturnPathOrFallback,
} from "./wecomOAuth";
import { resolveApplicationId, wecomLogin, type WecomMsOrgClientOptions } from "./wecomMsOrgApi";
import {
  getWecomMsOrgToken,
  setWecomMsOrgToken,
  type WecomMsOrgTokenStorageMode,
} from "./tokenStorage";

/** 换票页提示：避免静态 message 在 Vite+antd-mobile 等组合下抛错顶飞 ErrorBoundary */
function toastError(text: string): void {
  try {
    void import("antd")
      .then((m) => {
        try {
          m.message.error(text);
        } catch (e) {
          console.error("[WecomMsOrgCallbackPage]", text, e);
        }
      })
      .catch(() => {
        console.error("[WecomMsOrgCallbackPage]", text);
      });
  } catch (e) {
    console.error("[WecomMsOrgCallbackPage]", text, e);
  }
}

function wecomLoginErrorMessage(e: unknown): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as {
      message?: string;
      code?: string;
      details?: object;
    } | undefined;
    if (data?.message) {
      if (data.code === "APPLICATION_ASSIGNEE_DENIED" && data.details != null) {
        try {
          const compact = JSON.stringify(data.details);
          if (compact.length <= 900) {
            return `${data.message}（${compact}）`;
          }
          return `${data.message}（诊断信息过长，请打开开发者工具查看 wecom-login 响应体 details）`;
        } catch {
          return data.message;
        }
      }
      return data.message;
    }
    if (e.response?.status === 403) {
      return "服务器拒绝访问（403）。若提示使用人或权限掩码，请在组织管理后台为该应用配置使用人与功能权限。";
    }
  }
  return e instanceof Error ? e.message : "企微登录失败";
}

const wecomOAuthCodeInflight = new Set<string>();

export type WecomMsOrgCallbackPageProps = {
  /**
   * 由宿主页面注入的 `navigate`（与当前 SPA 使用同一份 react-router-dom）。
   * Vite 以 file: 链接 workspace 包时，包内 `useNavigate` 可能打到另一份模块实例，读不到
   * `<BrowserRouter>` 的 LocationContext，首屏即抛错（min 栈里的 Ko/sne）。
   */
  navigate?: NavigateFunction;
  /** 与 `navigate` 成对传入；来自宿主 `useSearchParams()[0]` */
  searchParams?: URLSearchParams;
  /** 换票成功后 navigate 目标，默认 / */
  afterLoginNavigateTo?: string;
  /** 失败时 navigate 目标，默认 / */
  onErrorNavigateTo?: string;
  tokenKey?: string;
  /** 写入 token 的存储范围，默认 both */
  tokenStorage?: WecomMsOrgTokenStorageMode;
} & WecomMsOrgClientOptions;

type InnerProps = WecomMsOrgCallbackPageProps & {
  navigate: NavigateFunction;
  searchParams: URLSearchParams;
};

const WecomMsOrgCallbackPageInner: React.FC<InnerProps> = ({
  navigate,
  searchParams,
  afterLoginNavigateTo = "/",
  onErrorNavigateTo = "/",
  tokenKey = "token",
  tokenStorage = "both",
  applicationId,
  msOrgApiBaseUrl,
  wecomRedirectUri,
  oauthStatePrefix,
}) => {
  const clientOpts = React.useMemo<WecomMsOrgClientOptions>(
    () => ({
      applicationId,
      msOrgApiBaseUrl,
      wecomRedirectUri,
      oauthStatePrefix,
    }),
    [applicationId, msOrgApiBaseUrl, wecomRedirectUri, oauthStatePrefix],
  );

  React.useEffect(() => {
    const run = async () => {
      const code = (searchParams.get("code") || "").trim();
      if (!code) {
        toastError("企微回调缺少 code");
        clearWecomOauthReturnPath();
        navigate(afterLoginNavigateTo, { replace: true });
        return;
      }
      if (wecomOAuthCodeInflight.has(code)) {
        return;
      }
      wecomOAuthCodeInflight.add(code);

      const appId = resolveApplicationId(clientOpts);
      if (!appId) {
        toastError("未配置 REACT_APP_MSORG_APPLICATION_ID");
        clearWecomOauthReturnPath();
        navigate(afterLoginNavigateTo, { replace: true });
        return;
      }
      const res = await wecomLogin(
        {
          code,
          applicationId: appId,
        },
        clientOpts,
      );

      if (!res.success || !res.token) {
        throw new Error(res.message || "企微登录失败");
      }

      setWecomMsOrgToken(tokenKey, res.token, tokenStorage);
      const roundtrip = getWecomMsOrgToken(tokenKey, tokenStorage);
      if (roundtrip !== res.token) {
        setWecomOAuthFail(
          "登录态无法写入浏览器存储（企微 WebView 可能禁用 localStorage/sessionStorage），请尝试升级企微或用系统浏览器打开链接。",
          "STORAGE_WRITE_FAILED",
        );
        toastError("登录态无法保存到本机，请重试或换端打开");
        clearWecomOauthReturnPath();
        navigate(onErrorNavigateTo, { replace: true });
        return;
      }
      clearOauthAttemptFlag();
      clearWecomOAuthFail();
      navigate(takeWecomOauthReturnPathOrFallback(afterLoginNavigateTo), { replace: true });
    };

    void run().catch((e: unknown) => {
      const msg = wecomLoginErrorMessage(e);
      const errCode =
        axios.isAxiosError(e) && e.response?.data && typeof e.response.data === "object"
          ? String((e.response.data as { code?: string }).code || "")
          : "";
      setWecomOAuthFail(msg, errCode || undefined);
      toastError(msg);
      clearWecomOauthReturnPath();
      navigate(onErrorNavigateTo, { replace: true });
    });
  }, [
    searchParams,
    navigate,
    afterLoginNavigateTo,
    onErrorNavigateTo,
    tokenKey,
    tokenStorage,
    clientOpts,
  ]);

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <span style={{ color: "#666", fontSize: 15 }}>企微登录处理中…</span>
    </div>
  );
};

/** 仅在未注入 navigate 时使用；尽量在宿主页注入以避免双份 router 模块 */
const WecomMsOrgCallbackPageWithRouterHooks: React.FC<
  Omit<WecomMsOrgCallbackPageProps, "navigate" | "searchParams">
> = (props) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  return (
    <WecomMsOrgCallbackPageInner
      {...props}
      navigate={navigate}
      searchParams={searchParams}
    />
  );
};

/**
 * 企微 OAuth 回调页：用 code 调 msOrg POST /auth/wecom-login，写入 token 后跳转。
 * CRM 等 Vite+workspace 场景请传入 `navigate` 与 `searchParams`。
 */
export const WecomMsOrgCallbackPage: React.FC<WecomMsOrgCallbackPageProps> = ({
  navigate: navigateProp,
  searchParams: searchParamsProp,
  ...rest
}) => {
  if (navigateProp != null && searchParamsProp != null) {
    return (
      <WecomMsOrgCallbackPageInner
        {...rest}
        navigate={navigateProp}
        searchParams={searchParamsProp}
      />
    );
  }
  return <WecomMsOrgCallbackPageWithRouterHooks {...rest} />;
};
